import styled from '@emotion/styled'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { IconButton, Snackbar, Typography } from '@mui/material'
import Alert, { AlertProps } from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import React, { useEffect } from 'react'
import { getPxAsRem } from 'shared/utils'

interface ToastAlertProps extends AlertProps {
    onClose: () => void
    title: string
    message: string
    timestamp?: string
    timeout?: number
    actionButtonTitle?: string
    onActionButtonClick?: () => void
    inline?: boolean
}

const StyledAlert = styled(Alert, {
    shouldForwardProp: (name) => name !== 'displayInline',
})<AlertProps & { displayInline?: boolean }>`
    min-width: ${getPxAsRem(300)};
    align-items: ${({ displayInline }) => (displayInline ? 'center' : '')};

    & .MuiAlert-action {
        padding-top: 0;

        & .MuiButton-root {
            margin-top: ${getPxAsRem(6)};
        }
    }

    & .MuiTypography-root {
        margin-bottom: 0;
    }

    & .MuiSvgIcon-root {
        fill: white;
    }
`

const Timestamp = styled.div`
    margin-top: ${getPxAsRem(24)};
`

const ToastAlert: React.FC<ToastAlertProps> = ({
    timeout,
    actionButtonTitle,
    onActionButtonClick,
    title,
    message,
    timestamp,
    id,
    inline,
    onClose,
    ...props
}) => {
    useEffect(() => {
        if (timeout) {
            const timer = setTimeout(() => {
                onClose()
            }, timeout)
            return () => clearTimeout(timer)
        }
    }, [timeout, onClose])

    return (
        <Snackbar
            open={true}
            autoHideDuration={timeout || undefined}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <StyledAlert
                {...props}
                displayInline={inline}
                onClose={onClose}
                variant="filled"
                action={
                    <>
                        <IconButton aria-label="close" onClick={onClose}>
                            <CloseOutlinedIcon fill="white" />
                        </IconButton>
                    </>
                }
            >
                <AlertTitle>
                    {title}{' '}
                    {inline && (
                        <Typography
                            variant="body2"
                            sx={{ display: 'inline', color: 'white' }}
                        >
                            {message}
                        </Typography>
                    )}
                </AlertTitle>
                {!inline && message}
                {actionButtonTitle && (
                    <Typography
                        variant="body2"
                        onClick={onActionButtonClick}
                        sx={{
                            color: 'text.primary',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            mt: 0.75,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                    >
                        {actionButtonTitle}
                    </Typography>
                )}
                {timestamp && <Timestamp>{timestamp}</Timestamp>}
            </StyledAlert>
        </Snackbar>
    )
}

export default ToastAlert
